import { ExperimentOutlined } from "@ant-design/icons";
import { Button, Form, Tabs } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ZSpecification } from "src/business/specification";
import { classNames } from "src/common/classNames";
import { onError } from "src/common/onError";
import { ChangeFlag } from "src/components/ChangeFlag";
import { LoaderBox } from "src/components/LoaderBox";
import { msgQueueStore } from "src/components/MsgQueue";
import { PageBlocker } from "src/components/PageBlocker";
import { PageLayout } from "src/components/PageLayout";
import { ObjectCardHeader } from "src/pages/management/objects/common/ObjectCardHeader";
import * as layoutStyles from "src/pages/management/objects/common/ObjectCardLayout.module.less";
import { ObjectCardTabPane } from "src/pages/management/objects/common/ObjectCardTabPane";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";

import { SysDataFormItems } from "../../../../../business/sysData/SysDataFormItems";
import { SpecLinksCollapseList } from "../SpecLinksView/SpecLinksCollapseList";
import { FormPreviewModal } from "./FormPreviewModal";
import { ManagementSpecCommonCardForm } from "./ManagementSpecCommonCardForm";
import {
  ManagementSpecCommonCardStore,
  SpecTabKey,
} from "./ManagementSpecCommonCardStore";
import { SpecEditorFormV2 } from "./SpecEditorFormV2";

type PropsManagementSpecCommonCard = {
  listUrl: PageUrl;
  cardUrl: PageUrl;
  pageStore: ManagementSpecCommonCardStore;
};

export const ManagementSpecCommonCard: React.FC<PropsManagementSpecCommonCard> =
  observer((props) => {
    const { listUrl, pageStore, cardUrl } = props;
    const { id = "new" } = useParams();
    React.useEffect(() => {
      pageStore.init(id).catch(onError);
    }, [id]);
    return (
      <PageLayout title={pageStore.crumbs}>
        <LoaderBox
          remoteData={pageStore.data}
          drawReady={(data) => (
            <Inner
              data={data}
              pageStore={pageStore}
              cardUrl={cardUrl}
              listUrl={listUrl}
            />
          )}
        />
      </PageLayout>
    );
  });

type PropsInner = {
  data: ZSpecification;
  pageStore: ManagementSpecCommonCardStore;
  cardUrl: PageUrl;
  listUrl: PageUrl;
};

const Inner: React.FC<PropsInner> = observer(
  ({ data, pageStore, cardUrl, listUrl }) => {
    const { activeTab, isNew, tabsItems, editorVersion } = pageStore;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const finish = async (values: unknown) => {
      try {
        if (isNew) {
          const res = await pageStore.create(values);
          msgQueueStore.add({
            type: "success",
            message: "Создана новая спецификация",
          });
          navigate(makeUrl(cardUrl, { id: res.id }), { replace: true });
        } else {
          await pageStore.save(values);
          msgQueueStore.add({ type: "info", message: "Данные сохранены" });
        }
      } catch (error) {
        pageStore.setActiveTab("general");
        throw error;
      }
    };

    return (
      <div className={layoutStyles.formColumnRoot}>
        <PageBlocker changed={() => pageStore.isChanged} />
        <ObjectCardHeader
          title={pageStore.pageTitle}
          submitText="Сохранить"
          submitLoading={pageStore.saving}
          onSubmitClick={() => form.submit()}
        >
          <ChangeFlag changed={pageStore.isChanged} />
          {/* если попросят убрать предупреждение о несохранённых данных при нажатии на эту ссылку, 
          то нужно в onClick вызвать pageStore.setIsChanged(false) */}
          <Link to={makeUrl(listUrl)}>
            <Button>Отменить</Button>
          </Link>
        </ObjectCardHeader>
        <div className={layoutStyles.tabsContainer}>
          {!isNew && (
            <Tabs
              items={tabsItems}
              activeKey={activeTab}
              onChange={(key) => pageStore.setActiveTab(key as SpecTabKey)}
              tabBarExtraContent={
                <Button
                  size="small"
                  icon={<ExperimentOutlined />}
                  onClick={() => pageStore.onTest(form.getFieldsValue())}
                >
                  Тестовый просмотр
                </Button>
              }
            />
          )}
          <ObjectCardTabPane
            className={layoutStyles.tabContent}
            hidden={activeTab !== "general"}
            hiddenClassName={layoutStyles.hidden}
          >
            {editorVersion === "v2" && (
              <SpecEditorFormV2
                store={pageStore}
                form={form}
                onFinish={(values) => finish(values).catch(onError)}
              />
            )}
            {editorVersion === "v1" && (
              <ManagementSpecCommonCardForm
                form={form}
                srcData={data}
                pageStore={pageStore}
                onFinish={(values) => finish(values).catch(onError)}
              />
            )}
          </ObjectCardTabPane>

          <ObjectCardTabPane
            className={classNames([
              layoutStyles.tabContent,
              layoutStyles.tabPaneInset,
            ])}
            hidden={isNew || activeTab !== "links"}
            hiddenClassName={layoutStyles.hidden}
          >
            <LoaderBox
              remoteData={pageStore.linksData}
              drawReady={(linksData) => (
                <SpecLinksCollapseList linksData={linksData} />
              )}
            />
          </ObjectCardTabPane>

          <ObjectCardTabPane
            className={`${layoutStyles.tabContent} ${layoutStyles.commonBlock}`}
            hidden={isNew || activeTab !== "systemData"}
            hiddenClassName={layoutStyles.hidden}
          >
            <SysDataFormItems data={data} />
          </ObjectCardTabPane>
        </div>
        <FormPreviewModal
          store={pageStore.testMForm}
          close={() => pageStore.setTestMForm(null)}
        />
      </div>
    );
  },
);
